import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Home, LayoutGrid, Compass, Heart, User } from 'lucide-react';
import { cn } from '@/lib/utils';
import { shouldShowBottomNav, isNavItemActive } from '@/lib/navigationUtils';
import { useIsMobile } from '@/hooks/use-mobile';
import { useCurrentUserSafe } from '@/contexts/UserContext';

/**
 * BottomNavBar - Centralized mobile navigation for buyer/public pages
 * 
 * Now uses UserContext instead of calling getUser() directly.
 */
export default function BottomNavBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('common');
  const isMobile = useIsMobile();
  const userContext = useCurrentUserSafe();
  const user = userContext?.user;

  // Don't render if shouldn't be visible
  if (!shouldShowBottomNav(location.pathname, isMobile)) {
    return null;
  }

  const navItems = [
    { 
      icon: Home, 
      label: t('nav.home', 'Home'), 
      path: '/' 
    },
    { 
      icon: LayoutGrid, 
      label: t('nav.categories', 'Categories'), 
      path: '/categories' 
    },
    { 
      icon: Compass, 
      label: t('nav.explore', 'Explore'), 
      path: '/explore' 
    },
    { 
      icon: Heart, 
      label: t('nav.favorites', 'Favorites'), 
      path: '/wishlist' 
    },
    { 
      icon: User, 
      label: t('nav.profile', 'Profile'), 
      path: user ? '/account' : '/auth' 
    },
  ];

  return (
    <nav
      className="app-bottom-nav md:hidden fixed left-0 right-0 bottom-0 z-[1000] bg-background border-t border-border"
      style={{ 
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        boxShadow: '0 -2px 12px 0 rgba(0, 0, 0, 0.08)'
      }}
    >
      <div className="flex items-center justify-around px-2 py-3 max-w-screen-sm mx-auto h-16">
        {navItems.map((item, index) => {
          const isActive = isNavItemActive(item.path, location.pathname);
          return (
            <button
              key={index}
              onClick={() => navigate(item.path)}
              className={cn(
                "flex flex-col items-center justify-center gap-1 flex-1 py-1.5 px-1 rounded-lg transition-all duration-200",
                "active:scale-95 relative"
              )}
              aria-label={item.label}
              aria-current={isActive ? 'page' : undefined}
            >
              {/* Active indicator - top bar */}
              {isActive && (
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-8 h-[3px] bg-primary rounded-full" />
              )}
              
              <div className="relative flex items-center justify-center">
                <item.icon 
                  className={cn(
                    "w-6 h-6 transition-all duration-200",
                    isActive ? "text-primary" : "text-[#6B7280]"
                  )}
                  strokeWidth={2}
                  fill={isActive ? "currentColor" : "none"}
                />
              </div>
              <span className={cn(
                "text-[11px] font-medium transition-all duration-200 leading-tight mt-0.5",
                isActive ? "text-primary font-semibold" : "text-[#6B7280]"
              )}>
                {item.label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
